import * as Sentry from '@sentry/nextjs';
import { Routes } from '@common/constants';
import { analytics, formatPrice } from '@features/analytics';
import { injectTranslations, TranslationsFunc } from '@lang';
import { some } from '@utils/some';
import { Component } from 'react';
import { BasketDetails } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import { removeOrderReferenceCookie } from 'shared-utils';

type BasketNotificatorProps = {
  t: TranslationsFunc;
  basketDetails: BasketDetails;
  fetchBasketDetails: (propertyId: number, basketId: string, language: string) => Promise<void>;
  deleteBasketNotification: (
    propertyId: number,
    basketId: string,
    notificationId: number
  ) => Promise<void>;
  onExpiredItemRemove: (message: { title: string; message: string; autoDismiss: number }) => void;
  clearBasket: () => void;
  propertyId: number;
  language: string;
  property: Property;
  router?: {
    pathname: string;
    push: (route: { pathname: string }) => void;
  };
};

const ROUTER_REDIRECT_DELAY_MS = 1000;

const CHECKOUT_ROUTES: string[] = [
  Routes.CHECKOUT,
  Routes.CHECKOUT_BILLING,
  Routes.CHECKOUT_PAYMENT,
  Routes.CHECKOUT_PRODUCT_DELIVERY,
  Routes.CHECKOUT_SIGNIN,
];

class BasketNotificator extends Component<BasketNotificatorProps> {
  interval: number;

  constructor(props) {
    super(props);
    this.interval = 0;
    this.handleBasketClear = this.handleBasketClear.bind(this);
    this.handleBasketNotifications = this.handleBasketNotifications.bind(this);
    this.deferExecution = this.deferExecution.bind(this);
  }

  componentDidMount() {
    this.deferExecution();
  }

  componentWillUnmount() {
    clearTimeout(this.interval);
    document.removeEventListener('visibilitychange', this.handleBasketClear);
  }

  deferExecution() {
    this.interval = setTimeout(this.handleBasketClear, 1000) as unknown as number;
  }

  async handleBasketNotifications() {
    const {
      t,
      basketDetails,
      fetchBasketDetails,
      deleteBasketNotification,
      onExpiredItemRemove,
      propertyId,
      language,
      router,
    } = this.props;

    analytics.track({
      event: 'basket_expired',
      params: {
        currency: basketDetails.total.currencyCode,
        value: formatPrice(basketDetails.total.amount),
        basket_id: basketDetails.id,
      },
    });

    const { notifications } = basketDetails;
    if (!notifications) {
      return;
    }

    // fire notification
    onExpiredItemRemove({
      title: t('basket.followingItemsExpired'),
      message: notifications.map((n) => n.metadata.name).join(', '),
      autoDismiss: 0,
    });

    const deleteNotificationRequests = notifications.map((n) =>
      deleteBasketNotification(propertyId, basketDetails.id, n.id)
    );

    await Promise.all(deleteNotificationRequests);
    await fetchBasketDetails(propertyId, basketDetails.id, language);

    const isOnCheckoutPage = CHECKOUT_ROUTES.includes(router?.pathname ?? '');

    if (isOnCheckoutPage) {
      setTimeout(() => {
        removeOrderReferenceCookie();
        router?.push({ pathname: Routes.BASKET });
      }, ROUTER_REDIRECT_DELAY_MS);

      return;
    }
  }

  async handleBasketClear() {
    try {
      const {
        basketDetails,
        clearBasket,
        language,
        propertyId,
        fetchBasketDetails,
        router,
        t,
        onExpiredItemRemove,
      } = this.props;

      if (!basketDetails?.id) {
        this.deferExecution();
        return;
      }

      if (basketDetails.notifications?.length) {
        await this.handleBasketNotifications();

        if (!this.props.basketDetails.items?.length) {
          clearBasket();
        }

        this.deferExecution();
        return;
      }

      if (!basketDetails.items?.length) {
        clearBasket();
        this.deferExecution();
        return;
      }

      const expiredItems = basketDetails.items?.filter(
        (item) => item.expireAt && Date.now() > Date.parse(item.expireAt)
      );

      if (some(expiredItems)) {
        onExpiredItemRemove({
          title: t('basket.followingItemsExpired'),
          message: expiredItems.map((item) => item.name).join(', '),
          autoDismiss: 0,
        });

        await fetchBasketDetails(propertyId, basketDetails.id, language);

        const isOnCheckoutPage = CHECKOUT_ROUTES.includes(router?.pathname ?? '');

        if (isOnCheckoutPage) {
          setTimeout(() => {
            removeOrderReferenceCookie();
            router?.push({ pathname: Routes.BASKET });
          }, ROUTER_REDIRECT_DELAY_MS);

          return;
        }
      }

      this.deferExecution();
    } catch (error) {
      Sentry.captureException(error);
      this.deferExecution();
    }
  }

  render() {
    return null;
  }
}

export default injectTranslations(BasketNotificator);
